"use client";

import { useEffect, useRef, useState } from "react";
import type { Voice } from "@/lib/creatorVoice/api";

const TIER_STYLES: Record<"free" | "premium" | "pro", { avatar: string; badge: string; label: string }> = {
  free: {
    avatar: "from-slate-400 to-slate-500",
    badge: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
    label: "Free",
  },
  premium: {
    avatar: "from-amber-400 to-amber-500",
    badge: "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400",
    label: "★ Premium",
  },
  pro: {
    avatar: "from-rose-500 to-fuchsia-500",
    badge: "bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400",
    label: "🔒 Pro",
  },
};

function voiceTier(v: Voice): "free" | "premium" | "pro" {
  if (v.tier === "pro") return "pro";
  if (v.is_premium) return "premium";
  return "free";
}

export default function VoiceCard({ voice, selected, onSelect }: { voice: Voice; selected: boolean; onSelect: () => void }) {
  const tier = voiceTier(voice);
  const style = TIER_STYLES[tier];
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onEnded = () => setPlaying(false);
    audio.addEventListener("ended", onEnded);
    return () => audio.removeEventListener("ended", onEnded);
  }, []);

  function togglePreview(e: React.MouseEvent) {
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.currentTime = 0;
      audio.play().catch(() => {});
      setPlaying(true);
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      className={`flex flex-col items-center gap-2 rounded-2xl border p-3 text-center transition cursor-pointer ${
        selected
          ? "border-rose-400 bg-rose-50/60 dark:border-rose-600 dark:bg-rose-500/10"
          : "border-slate-200/80 bg-white/60 hover:border-slate-300 dark:border-slate-800 dark:bg-[#0f172a]/60 dark:hover:border-slate-700"
      }`}
    >
      <div className="relative">
        <div className={`flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br ${style.avatar} text-white shadow-sm`}>
          <svg viewBox="0 0 24 24" fill="currentColor" className="h-7 w-7">
            <path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0 2c-4.4 0-8 2.2-8 5v1a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-1c0-2.8-3.6-5-8-5Z" />
          </svg>
        </div>
        <button
          type="button"
          onClick={togglePreview}
          aria-label={playing ? "Pause preview" : "Play preview"}
          className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-white text-rose-600 shadow ring-1 ring-slate-200 transition hover:scale-110 dark:bg-[#0f172a] dark:text-rose-400 dark:ring-slate-700"
        >
          {playing ? (
            <svg viewBox="0 0 24 24" fill="currentColor" className="h-3.5 w-3.5">
              <path d="M6 5h4v14H6V5Zm8 0h4v14h-4V5Z" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="currentColor" className="h-3.5 w-3.5">
              <path d="M8 5v14l11-7L8 5Z" />
            </svg>
          )}
        </button>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio ref={audioRef} src={voice.sample_url} preload="none" className="hidden" />
      </div>

      <p className="text-xs font-medium leading-tight text-slate-900 dark:text-white">{voice.label.replace(/\s*\((.*?)\)\s*$/, "")}</p>
      <p className="text-[11px] text-slate-500 dark:text-slate-400">{voice.gender} · {voice.language_label}</p>
      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${style.badge}`}>{style.label}</span>
    </div>
  );
}
